import React, { useMemo, useState } from "react";
import {
  Box,
  Typography,
  Button,
  CircularProgress,
  IconButton,
} from "@mui/material";
import { Formik, Form } from "formik";
import * as Yup from "yup";
import { toast } from "react-toastify";
import dayjs from 'dayjs';
import "../uwin.css";
import CloseIcon from "@mui/icons-material/Close";
// Pure utility functions - no API calls, only data shaping and validation.
// The VaccinationModule's own utils barrel is used here because dataTransformers.js
// is the canonical source for getInitialFormValues, validateCurrent/Previous, etc.
import {
  createVaccineIdentityHelpers,
  createVaccineIdentityRegistry,
  createVaccineIdentityRegistryFromVaccines,
  validateCurrentVaccinations,
  validatePreviousVaccinations,
  prepareFormDataWithMetadata,
  getInitialFormValues,
} from "./vaccinationUtils";

// checkAgeWindow drives child current eligibility filtering inside this component.
// It lives in the scenario layer so the rule is defined exactly once.
import { checkAgeWindow, calculateChildAgeInDays, getChildCurrentDoseStatus } from "./scenarios/children-current";

// Child components - pure presentation, no API concerns
import MemberCard from "../memberVaccination/MemberCard";
import VaccinationTabs from "./VaccinationTabs";
import { decryptOpenSSLAES } from "../uwin-decrypt";

import { cascadeUncheckIneligibleSubsequentDoses } from "./vaccinationUtils/previousDoseCascade";
import { getPWCurrentDoseStatus } from "./scenarios/pregnant-women-current";

// API service - createVaccinationRecord is the only API interaction this
// component performs, and only when isStepperMode is false.
import { createVaccinationRecord } from "./services/vaccinationApiService";

// ============================================================================
// VACCINATIONCONTAINER - FOCUSED VACCINATION WIDGET
// ============================================================================
//
// RESPONSIBILITIES (what this component owns):
//   - Current / Previous tab switching and dose selection UI
//   - Form validation for vaccination selections
//   - Formik state for vaccine selections and dose dates
//   - Calling createVaccinationRecord (standalone mode) or returning
//     materialDetails via onFormComplete (stepper mode)
//
// NOT RESPONSIBILITIES (handled by the parent / data-fetching layer):
//   - API calls for villages, vaccines, ASHA users, beneficiary profile
//   - Location dropdown state (villages, healthVillages, wards, ashaUsers)
//   - Beneficiary type detection from the profile API response
//   - Any state that is not related to which vaccines/doses are selected
//
// PROPS CONTRACT:
//   Required data (pre-fetched by parent, passed in as plain arrays/objects):
//     vaccines              {Array}   - Transformed vaccine list (transformVaccineData output)
//     currentVaccinations   {Array}   - Eligible current vaccination list
//     previousVaccinations  {Array}   - Eligible previous vaccination list
//     profileData           {Object}  - { lmc_date } for PW | { dob, childAgeInDays } for CHILD
//     administeredVaccinations {Array} - Raw vaccination history from profile API
//     villages              {Array}   - Transformed village list (for payload assembly)
//     healthVillages        {Array}   - Transformed health village list (for payload assembly)
//     wards                 {Array}   - Transformed ward list (for payload assembly)
//     ashaUsers             {Array}   - Transformed ASHA user list (NOT rendered here - passed
//                                       to LocationDropdowns rendered by the parent)
//
//   Configuration:
//     beneficiaryData       {Object}  - Raw beneficiary row from session list (session_id, etc.)
//     beneficiaryType       {string}  - 'PW' | 'CHILD'
//     allowedTabs           {Array}   - ['current'] | ['previous'] | ['current', 'previous']
//     isDataLoaded          {boolean} - Loading gate: show spinner until parent has all data
//
//   Callbacks:
//     onFormComplete        {Function} - Stepper mode: receives materialDetails when form submits
//     onContinue            {Function} - Standalone mode: called after successful API response
//     toggleDrawer          {Function} - Closes the drawer from the header X button
//
//   Rendering control:
//     isStepperMode         {boolean} - When true, skips API call and calls onFormComplete instead
//     locationFormValues    {Object}  - Formik values for location fields (lgd_village, etc.)
//                                       injected from the parent's own form so the payload
//                                       assembly in createVaccinationRecord picks them up.
// ============================================================================

const VaccinationContainer = ({
  // Pre-fetched data - NO API calls inside this component
  vaccines = [],
  currentVaccinations: currentVaccinationsProp = [],
  previousVaccinations: previousVaccinationsProp = [],
  profileData = null,
  administeredVaccinations = [],
  allUipMaterialsRaw = [],
  villages = [],
  healthVillages = [],
  wards = [],
  healthWards = [],

  // Beneficiary context
  beneficiaryData,
  beneficiaryType, // 'PW' | 'CHILD' - determined by parent, not auto-detected here

  // Tab configuration
  allowedTabs = ['current', 'previous'],

  // Loading state owned by parent (the data-fetching layer)
  isDataLoaded = false,

  // Location form values - injected by parent so payload assembly can read them
  // without this component needing to own its own location dropdowns
  locationFormValues = {},

  // Callbacks
  onContinue,
  toggleDrawer,
  onFormComplete,
  isStepperMode = false,

  isCurrentFailed = false,
  isPrevFailed = false,

  // Called with the new tab index whenever the user switches tabs.
  // The parent (MemberVaccination) uses this to show/hide LocationDropdowns.
  onTabChange,
}) => {
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Tab index: always starts at 0, VaccinationTabs maps indices to tab types
  const [currentTab, setCurrentTab] = useState(0);

  // Map tab index to tab type via allowedTabs (previous-only: index 0 = 'previous')
  const getActiveTabType = () =>
    allowedTabs[currentTab] ?? (currentTab === 0 ? 'current' : 'previous');

  const isActiveCurrentTab = () => getActiveTabType() === 'current';
  const isActivePreviousTab = () => getActiveTabType() === 'previous';

  const getMetadataTabIndex = () => (isActivePreviousTab() ? 1 : 0);

  const isAfterEdd = beneficiaryType === 'PW' &&
                     profileData?.edd_date &&
                     dayjs().isAfter(dayjs(profileData.edd_date), 'day');

  // ============================================================================
  // CHILD CURRENT VACCINATION ELIGIBILITY FILTER
  //
  // For CHILD beneficiaries the parent passes the raw currentVaccinations list
  // from the API.  We filter it here by age eligibility using the canonical
  // checkAgeWindow function from the scenario layer.
  //
  // For PW beneficiaries we use the list as-is (the API already does PW filtering).
  // ============================================================================
  const getEffectiveCurrentVaccinations = () => {
    if (beneficiaryType !== 'CHILD' || !profileData?.dob) {
      return currentVaccinationsProp;
    }

    const childAgeInDays =
      profileData.childAgeInDays || calculateChildAgeInDays(profileData.dob);

    return currentVaccinationsProp.filter((material) => {
      const numDoses = material.no_of_doses || 1;
      // material.material holds the raw API object with lower/upper_age_limits;
      // fall back to material itself when the transformer flattens the shape.
      const materialRules = material.material || material;

      for (let doseIndex = 0; doseIndex < numDoses; doseIndex++) {
        if (checkAgeWindow(childAgeInDays, doseIndex, materialRules).pass) {
          return true;
        }
      }
      return false;
    });
  };

  // Derived arrays - computed once per render, no state needed
  const currentVaccinations = getEffectiveCurrentVaccinations();
  const previousVaccinations = previousVaccinationsProp;

  const vaccineIdentityRegistry = useMemo(() => {
    const masterRegistry = createVaccineIdentityRegistry(
      allUipMaterialsRaw || [],
      decryptOpenSSLAES
    );

    if (masterRegistry.byId.size > 0) {
      return masterRegistry;
    }

    return createVaccineIdentityRegistryFromVaccines(
      [...currentVaccinations, ...previousVaccinations],
      decryptOpenSSLAES
    );
  }, [allUipMaterialsRaw, currentVaccinations, previousVaccinations]);

  const identityMatchOptions = useMemo(
    () => ({
      warnOnMismatch: process.env.NODE_ENV !== "production",
    }),
    []
  );

  const identityContext = useMemo(
    () => ({
      registry: vaccineIdentityRegistry,
      matchOptions: identityMatchOptions,
      decryptFunction: decryptOpenSSLAES,
    }),
    [vaccineIdentityRegistry, identityMatchOptions]
  );

  const vaccineIdentity = useMemo(
    () =>
      createVaccineIdentityHelpers(
        vaccineIdentityRegistry,
        decryptOpenSSLAES,
        identityMatchOptions
      ),
    [vaccineIdentityRegistry, identityMatchOptions]
  );

  // ============================================================================
  // FORM EVENT HANDLERS
  // All handlers follow the pattern: receive Formik setFieldValue + current
  // values, return updated Formik state without touching any API or external state.
  // ============================================================================

  const handleTabChange = (event, newValue) => {
    setCurrentTab(newValue);
    // Notify the parent so it can react to the tab change (e.g., hide location
    // dropdowns on the Previous tab) without owning the tab state itself.
    if (onTabChange) {
      onTabChange(newValue);
    }
  };

  const handleSelectAllToggle = (checked, setFieldValue, values) => {
    setFieldValue("select_all_vaccines", checked);
    if (!checked) {
      const updatedCurrentVaccines = values.current_vaccines.map((vaccine) => ({
        ...vaccine,
        selected: false,
        selectedDose: null,
      }));
      setFieldValue("current_vaccines", updatedCurrentVaccines);
      return;
    }

    let isPentaSelectedOrWillBe = false;

    // First pass: Determine eligibility and check if Pentavalent will be selected
    const vaccinesWithEligibility = values.current_vaccines.map((vaccine) => {
      let eligibleDoseName = null;
      let isEligible = false;
      if (vaccine.availableDoses) {
        for (let i = 0; i < vaccine.availableDoses.length; i++) {
          const doseConfig = beneficiaryType === "PW" 
            ? getPWCurrentDoseStatus(vaccine, i, profileData, decryptOpenSSLAES, identityContext)
            : getChildCurrentDoseStatus(
                vaccine,
                i,
                profileData?.dob,
                administeredVaccinations,
                decryptOpenSSLAES,
                identityContext
              );
            
          if (doseConfig.status === "Eligible" || doseConfig.status === "enabled") {
            eligibleDoseName = vaccine.availableDoses[i].doseName;
            isEligible = true;
            break;
          }
        }
      }
      
      const isPentavalent = vaccineIdentity.isPentavalent(vaccine);
      if (isPentavalent && isEligible) {
        isPentaSelectedOrWillBe = true;
      }

      return {
        ...vaccine,
        isEligible,
        eligibleDoseName
      };
    });

    // Second pass: Respect mutual exclusions
    //   1. Pentavalent vs DPT primary (exact name, not Booster)
    //   2. MR vs MMR
    const isMmrWillBeSelected = vaccinesWithEligibility.some(
      (v) => vaccineIdentity.isMmr(v) && v.isEligible
    );

    const isDptPrimaryWillBeSelected = vaccinesWithEligibility.some(
      (v) => vaccineIdentity.isDptPrimary(v) && v.isEligible && !isPentaSelectedOrWillBe
    );
    const adminDptCount = (administeredVaccinations || []).filter(
      (r) => vaccineIdentity.isDptPrimary(r)
    ).length;
    const isDptBoosterBlocked =
      (adminDptCount > 0 && adminDptCount < 3) ||
      isDptPrimaryWillBeSelected;

    const updatedCurrentVaccines = vaccinesWithEligibility.map((vaccine) => {
      // Exact name match — must NOT match DPT-Booster
      const isDptPrimary = vaccineIdentity.isDptPrimary(vaccine);
      const isDptBooster = vaccineIdentity.isDptBooster(vaccine);
      const isMr  = vaccineIdentity.isMr(vaccine);

      const shouldSelect = vaccine.isEligible &&
        // Skip DPT primary if Pentavalent is eligible (Pentavalent takes priority)
        !(isDptPrimary && isPentaSelectedOrWillBe) &&
        !(isDptBooster && isDptBoosterBlocked) &&
        // MR can stay selected with MMR, but MR-2 cannot be selected with MMR.
        !(isMr && vaccine.eligibleDoseName === "MR-2" && isMmrWillBeSelected);

      return {
        ...vaccine,
        selected: shouldSelect,
        selectedDose: shouldSelect ? (vaccine.eligibleDoseName || vaccine.selectedDose) : null
      };
    });

    setFieldValue("current_vaccines", updatedCurrentVaccines);
  };

  const handleVaccineToggle = (vaccineId, checked, setFieldValue, values) => {
    const vaccineArray =
      isActiveCurrentTab() ? 'current_vaccines' : 'previous_vaccines';
    const updatedVaccines = values[vaccineArray].map((vaccine) => {
      if (vaccine.id === vaccineId) {
        if (isActiveCurrentTab()) {
          if (!checked) {
            return { ...vaccine, selected: false, selectedDose: null };
          } else {
            if (vaccine.availableDoses && vaccine.availableDoses.length === 1) {
              return { ...vaccine, selected: true, selectedDose: vaccine.availableDoses[0].doseName };
            }
          }
        } else {
          if (!checked) {
            const clearedDoses = vaccine.availableDoses
              ? vaccine.availableDoses.map((dose) => ({
                  ...dose,
                  selected: false,
                  administeredDate: null,
                }))
              : vaccine.availableDoses;
            return { ...vaccine, selected: false, availableDoses: clearedDoses };
          }
          if (vaccine.availableDoses && vaccine.availableDoses.length === 1) {
            const updatedDoses = vaccine.availableDoses.map((dose) => ({ ...dose, selected: true }));
            return { ...vaccine, selected: true, availableDoses: updatedDoses };
          }
        }
        return { ...vaccine, selected: checked };
      }
      return vaccine;
    });

    // Handle mutual deselection within the active tab only
    const toggledVaccine = values[vaccineArray].find(v => v.id === vaccineId);
    if (checked && toggledVaccine) {
      if (vaccineIdentity.isDptPrimary(toggledVaccine)) {
        // Uncheck DPT Booster if DPT Primary is checked
        updatedVaccines.forEach(v => {
          if (vaccineIdentity.isDptBooster(v) && v.selected) {
            v.selected = false;
            v.selectedDose = null;
            if (v.availableDoses) {
              v.availableDoses.forEach(d => { d.selected = false; d.administeredDate = null; });
            }
          }
        });
      }
      if (vaccineIdentity.isMmr(toggledVaccine)) {
        // Uncheck MR-2 if MMR is checked (within active tab only)
        updatedVaccines.forEach(v => {
          if (vaccineIdentity.isMr(v) && v.selected) {
            if (isActiveCurrentTab() && v.selectedDose === "MR-2") {
              v.selectedDose = null;
            } else if (isActivePreviousTab() && v.availableDoses && v.availableDoses[1]) {
              v.availableDoses[1].selected = false;
              v.availableDoses[1].administeredDate = null;
            }
          }
        });
      }
    }

    setFieldValue(vaccineArray, updatedVaccines);

    if (isActiveCurrentTab()) {
      const allSelected = updatedVaccines.every((v) => v.selected);
      setFieldValue("select_all_vaccines", allSelected);
    }
  };

  const handleDoseSelection = (vaccineId, selectedDose, setFieldValue, values) => {
    const updatedVaccines = values.current_vaccines.map((vaccine) =>
      vaccine.id === vaccineId ? { ...vaccine, selectedDose } : vaccine
    );

    // Mutual deselection for MR-2 vs MMR (current tab only)
    const toggledVaccine = values.current_vaccines.find(v => v.id === vaccineId);
    if (toggledVaccine && vaccineIdentity.isMr(toggledVaccine) && selectedDose === "MR-2") {
      updatedVaccines.forEach(v => {
        if (vaccineIdentity.isMmr(v) && v.selected) {
          v.selected = false;
          v.selectedDose = null;
        }
      });
    }

    setFieldValue("current_vaccines", updatedVaccines);
  };

  const handlePreviousDoseToggle = (
    vaccineId,
    doseIndex,
    checked,
    setFieldValue,
    values
  ) => {
    const updatedVaccines = values.previous_vaccines.map((vaccine) => {
      if (vaccine.id === vaccineId && vaccine.availableDoses) {
        const updatedDoses = vaccine.availableDoses.map((dose, index) => {
          // Update the toggled dose itself; clearing the checkbox also clears its date
          // so users can reset without typing in the calendar-only picker.
          if (index === doseIndex) {
            return checked
              ? { ...dose, selected: true }
              : { ...dose, selected: false, administeredDate: null };
          }
          // When unchecking, cascade: force-uncheck all subsequent doses
          // because they depend sequentially on the unchecked dose
          if (!checked && index > doseIndex) {
            return { ...dose, selected: false, administeredDate: null };
          }
          return dose;
        });
        return { ...vaccine, availableDoses: updatedDoses };
      }
      return vaccine;
    });

    // Mutual deselection for MR-2 vs MMR (previous tab only)
    if (checked) {
      const toggledVaccine = values.previous_vaccines.find(v => v.id === vaccineId);
      if (toggledVaccine && vaccineIdentity.isMr(toggledVaccine) && doseIndex === 1) {
        updatedVaccines.forEach(v => {
          if (vaccineIdentity.isMmr(v) && v.selected) {
            v.selected = false;
            if (v.availableDoses) {
              v.availableDoses.forEach(d => { d.selected = false; d.administeredDate = null; });
            }
          }
        });
      }
    }

    setFieldValue("previous_vaccines", updatedVaccines);
  };

  /**
   * Handle previous vaccination dose date change.
   * Cascades to later doses on the same vaccine: if a date makes them ineligible,
   * they are unchecked and cleared so they cannot leak into the submit payload.
   */
  const handlePreviousDoseDate = (
    vaccineId,
    doseIndex,
    date,
    setFieldValue,
    values
  ) => {
    const updatedVaccines = values.previous_vaccines.map((vaccine) => {
      if (vaccine.id !== vaccineId || !vaccine.availableDoses) {
        return vaccine;
      }

      const updatedDoses = vaccine.availableDoses.map((dose, index) =>
        index === doseIndex ? { ...dose, administeredDate: date } : dose
      );

      if (updatedDoses.length <= 1) {
        return { ...vaccine, availableDoses: updatedDoses };
      }

      const { doses: cascadedDoses } = cascadeUncheckIneligibleSubsequentDoses(
        vaccine,
        updatedDoses,
        doseIndex,
        beneficiaryType,
        profileData,
        administeredVaccinations,
        decryptOpenSSLAES,
        identityContext
      );

      return { ...vaccine, availableDoses: cascadedDoses };
    });

    setFieldValue("previous_vaccines", updatedVaccines);
  };

  // ============================================================================
  // FORM SUBMISSION
  //
  // Standalone mode  (isStepperMode = false):
  //   Calls createVaccinationRecord API directly, shows toast, calls onContinue.
  //
  // Stepper mode     (isStepperMode = true):
  //   Skips API call entirely. Calls onFormComplete with the prepared form data
  //   so the parent stepper can display a review card before committing.
  // ============================================================================
  const handleSubmit = async (values, { setSubmitting, resetForm }) => {
    try {
      setIsSubmitting(true);

      const vaccinationType = isActivePreviousTab() ? 'previous' : 'current';

      // -----------------------------------------------------------------------
      // GUARD 1: Mandatory location selection (Current tab only)
      // For Previous vaccinations the location / session-tag is irrelevant
      // because the dose was administered in the past, possibly at a different
      // facility or before the current system was in use.
      // -----------------------------------------------------------------------
      const { lgd_village, health_village, lgd_ward, health_ward, tag_to_session_site } = locationFormValues;
      if (
        vaccinationType === 'current' &&
        !tag_to_session_site &&
        !lgd_village &&
        !health_village &&
        !lgd_ward &&
        !health_ward
      ) {
        toast.error("Please select at least one location (Village / Ward) or tag to session site");
        return;
      }

      const validation =
        isActiveCurrentTab()
          ? validateCurrentVaccinations(values.current_vaccines)
          : validatePreviousVaccinations(values.previous_vaccines);

      if (!validation.isValid) {
        toast.error(validation.message);
        return;
      }

      // Check edd_date limit for previous vaccinations
      if (isActivePreviousTab() && profileData?.edd_date) {
        const edd = dayjs(profileData.edd_date);
        for (const vaccine of values.previous_vaccines) {
          if (vaccine.selected && vaccine.availableDoses) {
            for (const dose of vaccine.availableDoses) {
              if (dose.selected && dose.administeredDate) {
                if (dayjs(dose.administeredDate).isAfter(edd, 'day')) {
                  toast.error(`Vaccination date for ${vaccine.name} - ${dose.doseName} cannot be after the EDD (${edd.format('DD/MM/YYYY')})`);
                  return;
                }
              }
            }
          }
        }
      }

      // Merge the vaccination form values with any location values that the
      // parent injected.  The service layer (createVaccinationRecord) reads
      // lgd_village, health_village, lgd_ward, and asha_user_id from formData
      // to assemble the payload, so they must be present here.
      // locationFormValues is spread LAST so the outer Formik's location selections
      // (lgd_village, health_village, lgd_ward, health_ward, asha_user_id) always
      // take priority over anything that might exist in the inner form values.
      const mergedValues = { ...values, ...locationFormValues };

      const formDataWithMetadata = prepareFormDataWithMetadata(
        mergedValues,
        getMetadataTabIndex(),
        villages,
        healthVillages,
        wards,
        healthWards,
        beneficiaryData
      );

      if (isStepperMode && onFormComplete) {
        // Stepper mode: hand off data to parent review step, no API call yet
        toast.success("Proceeding to review...");
        onFormComplete(formDataWithMetadata);
        return;
      }

      // Standalone mode: call API immediately
      const response = await createVaccinationRecord(
        formDataWithMetadata,
        vaccinationType,
        null,
        beneficiaryData,
        villages,
        healthVillages,
        wards,
        healthWards
      );

      if (response.success) {
        toast.success(
          response.message ||
            `${vaccinationType} vaccination recorded successfully`
        );

        if (onContinue) {
          onContinue();
        } else {
          resetForm();
        }
      } else {
        throw new Error(
          response.message || 'Failed to record vaccination'
        );
      }
    } catch (error) {
      console.error("VaccinationContainer handleSubmit error:", error);
      toast.error(error.message || "Failed to record vaccination");
    } finally {
      setIsSubmitting(false);
      setSubmitting(false);
    }
  };

  // ============================================================================
  // FORM VALIDATION SCHEMA
  // Location fields are intentionally not required here because location
  // dropdowns are owned and validated by the parent form.
  // ============================================================================
  const validationSchema = Yup.object().shape({
    select_all_vaccines: Yup.boolean(),
    current_vaccines: Yup.array(),
    previous_vaccines: Yup.array(),
  });

  // ============================================================================
  // RENDER
  // ============================================================================

  return (
    <Box sx={{ maxWidth: 1200, margin: "0 auto", padding: 2 }}>
      {/* Header */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 2,
        }}
      >
      </Box>

      {/* Vaccination selection form
          NOTE: This Formik instance owns ONLY the vaccination fields
          (current_vaccines, previous_vaccines, select_all_vaccines).
          Location fields (lgd_village etc.) are owned by the parent form
          and injected via the locationFormValues prop at submission time. */}
      <Formik
        initialValues={getInitialFormValues(
          currentVaccinations,
          previousVaccinations
        )}
        validationSchema={validationSchema}
        onSubmit={handleSubmit}
        // enableReinitialize is intentionally false: once the user has started
        // selecting vaccines we do not want the form to reset if the parent
        // re-renders (e.g., a sibling state change in the parent).
        enableReinitialize={false}
      >
        {({ values, setFieldValue, isSubmitting: formikSubmitting }) => (
          <Form>
            {/* VaccinationTabs renders Current/Previous tabs, vaccine
                checkboxes, dose rows, and date pickers.  Zero business logic
                lives here - all rules are in the scenario hooks. */}
            <VaccinationTabs
              currentTab={currentTab}
              onTabChange={handleTabChange}
              values={values}
              setFieldValue={setFieldValue}
              currentVaccinations={currentVaccinations}
              previousVaccinations={previousVaccinations}
              isDataLoaded={isDataLoaded}
              onSelectAllToggle={(checked) =>
                handleSelectAllToggle(checked, setFieldValue, values)
              }
              onVaccineToggle={(vaccineId, checked) =>
                handleVaccineToggle(vaccineId, checked, setFieldValue, values)
              }
              onDoseSelection={(vaccineId, selectedDose) =>
                handleDoseSelection(vaccineId, selectedDose, setFieldValue, values)
              }
              onPreviousDoseToggle={(vaccineId, doseIndex, checked) =>
                handlePreviousDoseToggle(
                  vaccineId,
                  doseIndex,
                  checked,
                  setFieldValue,
                  values
                )
              }
              onPreviousDoseDate={(vaccineId, doseIndex, date) =>
                handlePreviousDoseDate(
                  vaccineId,
                  doseIndex,
                  date,
                  setFieldValue,
                  values
                )
              }
              allowedTabs={allowedTabs}
              beneficiaryType={beneficiaryType}
              profileData={profileData}
              administeredVaccinations={administeredVaccinations}
              decryptFunction={decryptOpenSSLAES}
              identityContext={identityContext}
              vaccineIdentity={vaccineIdentity}
              isCurrentFailed={isCurrentFailed}
              isPrevFailed={isPrevFailed}
              isAfterEdd={isAfterEdd}
            />

            {/* Submit button
                Disabled when ANY of the following is true:
                  1. isNoVaccineImageShowing — the active tab is showing the
                     no_vaccine.png empty-state (API failed + 0 vaccines).
                     This is a hard override: no submission when there is
                     nothing to submit.
                  2. isLocationNotSelected — neither any location dropdown
                     nor "Tag to session site" has been selected.
                  3. isSubmitting / formikSubmitting / !isDataLoaded —
                     standard loading / in-flight guards. */}
            {(() => {
              // Derive the active tab's vaccine list length from Formik values
              // (same source VaccinationTabs uses to decide when to render the image).
              const activeVaccineCount = isActiveCurrentTab()
                  ? (isAfterEdd ? 0 : (values.current_vaccines || []).length)
                  : (values.previous_vaccines || []).length;

              const isNoVaccinesAvailable = activeVaccineCount === 0;

              // Location / session-tag is only mandatory for the Current tab.
              // Previous vaccinations record historical doses where site context
              // is not required.
              const { lgd_village, health_village, lgd_ward, health_ward, tag_to_session_site } =
                locationFormValues;
              const isLocationNotSelected =
                isActiveCurrentTab() &&
                !tag_to_session_site &&
                !lgd_village &&
                !health_village &&
                !lgd_ward &&
                !health_ward;

              const isDisabled =
                isNoVaccinesAvailable ||
                isLocationNotSelected ||
                isSubmitting ||
                formikSubmitting ||
                !isDataLoaded;

              return (
                <Box sx={{ mt: 3, display: "flex", justifyContent: "center" }}>
                  <Button
                    type="submit"
                    variant="contained"
                    disabled={isDisabled}
                    sx={{
                      px: 4,
                      py: 1.5,
                      fontSize: "16px",
                      fontWeight: 600,
                      textTransform: "none",
                      "&.Mui-disabled": {
                        backgroundColor: "#cccccc",
                        color: "#666666",
                      }
                    }}
                  >
                    {isSubmitting || formikSubmitting ? (
                      <>
                        <CircularProgress size={20} sx={{ mr: 1 }} />
                        Processing...
                      </>
                    ) : isStepperMode ? (
                      "Save & Continue"
                    ) : (
                      `Record ${isActivePreviousTab() ? 'Previous' : 'Current'} Vaccination`
                    )}
                  </Button>
                </Box>
              );
            })()}
          </Form>
        )}
      </Formik>

      {/* Loading overlay - visible until the parent signals isDataLoaded */}
      {!isDataLoaded && (
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: 'rgba(255, 255, 255, 0.8)',
            zIndex: 1000,
          }}
        >
          <CircularProgress size={40} />
          <Typography sx={{ mt: 2, color: 'text.secondary' }}>
            Loading vaccination data...
          </Typography>
        </Box>
      )}
    </Box>
  );
};

export default VaccinationContainer;
